<template>
  <div v-if="disasterNo">
    <!-- 共通ヘッダー（タイトル/サブタイトル） -->
    <div class="content-header">
      <h1 class="page-title">
        <i class="fas fa-magnifying-glass"></i>
        モニタリングポスト
      </h1>
    </div>
    <error-message :error-object="errorObject" />
    <section>
      <!-- 検索部 -->
      <details :open="isOpen" ref="monitoring_post_search">
        <summary @click.prevent="toggleOpen">検索条件</summary>
        <div class="content-card compact">
          <div
            style="display:flex; flex-direction:column; min-width:300px; margin-left:30px; flex-wrap:wrap;"
          >
            <div style="display:flex; flex-direction:column;">
              <div
                style="display:flex; align-items:flex-start; gap:32px; flex-wrap:wrap;"
              >
                <div style="display:flex; align-items:center; gap:8px;">
                  <div class="vtf-field" style="width:120px;">
                    <input
                      v-model="searchFilters.stationName"
                      type="text"
                      placeholder=" "
                      maxlength="30"
                    />
                    <label>地点名</label>
                  </div>
                  <div class="hint">部分一致</div>
                </div>
                <div style="display:flex; align-items:center; gap:8px;">
                  <div class="vtf-field" style="width:120px;">
                    <input
                      v-model="searchFilters.address"
                      type="text"
                      placeholder=" "
                      maxlength="50"
                    />
                    <label>設置場所</label>
                  </div>
                  <div class="hint">部分一致</div>
                </div>
                <div
                  style="margin-top: 15px; font-weight:500; color:#666; font-size:12px;"
                >
                  原発からの直線距離
                </div>
                <div class="range-field">
                  <input
                    v-model="searchFilters.distanceFrom"
                    type="text"
                    name="distanceFrom"
                    maxlength="6"
                    autocomplete="off"
                    @input="validateRange"
                  />
                  <span>〜</span>
                  <input
                    v-model="searchFilters.distanceTo"
                    type="text"
                    name="distanceTo"
                    maxlength="6"
                    autocomplete="off"
                    @input="validateRange"
                  />
                  <span>km</span>
                </div>
              </div>
            </div>
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="btn-primary" @click="clickGetList">
              一覧表示
            </v-btn>
          </v-card-actions>
        </div>
      </details>

      <!-- 一覧部 -->
      <div class="content-card">
        <div
          style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap;"
        >
          <div
            style="display:flex; align-items:center; gap:16px; flex-wrap:wrap;"
        >
            <h2 class="card-title" style="margin:0;">モニタリングポスト一覧</h2>
          </div>
          <v-card-actions>
            <v-btn class="btn-primary" @click="exportExcel">
              Excel出力
            </v-btn>
          </v-card-actions>
        </div>
        <div class :style="{ height: tableHeight + 'px', overflow: 'auto' }">
          <table class="table adjustment-table mp-table">
            <thead>
              <tr>
                <th rowspan="2">地点名</th>
                <th rowspan="2">設置場所</th>
                <th colspan="2">原発からの</th>
                <th rowspan="2">配置場所</th>
                <th rowspan="2">市町地区</th>
              </tr>
              <tr>
                <th>方角</th>
                <th>直線距離</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(post, index) in sortedFacilities"
                :key="post.station_id + '_' + index"
              >
                <td>{{ post.station_name }}</td>
                <td>{{ post.station_address }}</td>
                <td>{{ post.direction_from_npp_name }}</td>
                <td>{{ post.distance_from_npp }} km</td>
                <td>{{ post.installation }}</td>
                <td>
                  <div v-for="area in post.areas" :key="area.key">
                    {{ area.muni_area_name }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    <ErrorDialog ref="errorDialog" />
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import messageUtils from "@/mixins/message-utils";
import { ErrorTodoMessages } from "@/js/constants/error-messages";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const adjustmentSystemRepository = RepositoryFactory.get("adjustmentSystem");
const radiationMonitoringRepository = RepositoryFactory.get(
  "radiationMonitoring"
);

const IS_AREA_TRUE = 1;
const LIST_MAX_HEIGHT = 598;
const LIST_MIN_HEIGHT = 458;

export default {
  name: "MonitoringPost",
  mixins: [messageUtils],
  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    ErrorDialog: () => import("@/components/adjustment/AdErrorDialog")
  },
  props: {
    disasterNo: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    overlay: false,
    isOpen: true,
    isAuth: false,
    searchFilters: {
      stationName: "",
      address: "",
      distanceFrom: null,
      distanceTo: null
    },
    sortKey: "",
    sortOrder: "asc",
    postList: []
  }),

  computed: {
    // 検索条件表示非表示の時の一覧の高さ調整
    tableHeight() {
      return this.isOpen ? LIST_MIN_HEIGHT : LIST_MAX_HEIGHT;
    },
    // 一覧のソート
    sortedFacilities() {
      const filtered = this.postList;
      if (!this.sortKey) return filtered;

      return [...filtered].sort((a, b) => {
        const aVal = a[this.sortKey];
        const bVal = b[this.sortKey];
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    }
  },

  async mounted() {
    await this.initialize();
  },

  methods: {
    // 初期処理
    async initialize() {
      this.clearMessage();
      this.overlay = true;
      try {
        /* 権限取得 */
        const payload = {
          disasterNo: this.disasterNo
        };
        await adjustmentSystemRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.isAuth = response.data.isAuth;
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("ユーザーの権限取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 検索条件開閉
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    validateRange(e) {
      const key = e.target.name;
      let txtValue = e.target.value.replace(/[^0-9.]/g, "");
      let value = parseFloat(txtValue);
      if (isNaN(value)) {
        this.searchFilters[key] = txtValue;
      }
      if (value < 0.0) txtValue = 0.0;
      if (value > 9999.9) txtValue = 9999.9;
      this.searchFilters[key] = txtValue;
    },
    // 一覧のソート処理
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },
    clearSort() {
      this.sortKey = "";
      this.sortOrder = "asc";
    },
    async clickGetList() {
      this.clearMessage();
      await this.getList();
    },
    // モニタリングポスト一覧取得
    async getList() {
      this.overlay = true;
      try {
        /* 放射線モニタリング一覧取得 */
        const payload = {
          stationName: this.searchFilters.stationName,
          address: this.searchFilters.address,
          distanceFrom: this.searchFilters.distanceFrom,
          distanceTo: this.searchFilters.distanceTo,
          isArea: IS_AREA_TRUE
        };
        await radiationMonitoringRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.postList = response.data.results;
            this.isOpen = false;
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("モニタリングポスト一覧の取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // EXCEL出力
    async exportExcel() {
      this.overlay = true;
      try {
        if (!this.postList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        /* タイムスタンプ算出 */
        let now = new Date();
        let year = now.getFullYear();
        let month = ("00" + (now.getMonth() + 1)).slice(-2);
        let day = ("00" + now.getDate()).slice(-2);
        let hour = ("00" + now.getHours()).slice(-2);
        let minute = ("00" + now.getMinutes()).slice(-2);
        let timeStamp = year + month + day + hour + minute;
        let timeStamp2 =
          year + "/" + month + "/" + day + " " + hour + ":" + minute;

        // 固定のExcelファイルを取得（publicフォルダに配置されていると仮定）
        const response = await fetch("/template/monitoring_post_list.xlsx");
        const arrayBuffer = await response.arrayBuffer();

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.getWorksheet(1); // 最初のシートを取得

        // 罫線スタイルを定義
        const borderStyle = {
          style: "thin",
          color: { argb: "FF000000" }
        };

        //4行目まではヘッダー
        const headerRowCount = 4;
        const colAreaNo = 7;
        let rowNo = headerRowCount;
        this.postList.forEach(data => {
          rowNo++;
          const row = worksheet.insertRow(rowNo, [
            rowNo - headerRowCount,
            data.station_name,
            data.station_address,
            data.direction_from_npp_name,
            data.distance_from_npp + " km",
            data.installation,
            data.areas.map(area => area.muni_area_name).join("\n")
          ]);

          // 各セルに罫線を設定
          row.eachCell(cell => {
            cell.border = {
              top: borderStyle,
              left: borderStyle,
              bottom: borderStyle,
              right: borderStyle
            };
          });

          // 市町地区にwrapTextを設定
          row.getCell(colAreaNo).alignment = { wrapText: true };
        });

        const cell = worksheet.getCell("G2");
        cell.value = "出力日時：" + timeStamp2;

        // 編集済みファイルをダウンロード
        const editedBuffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([editedBuffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "モニタリングポスト一覧_" + timeStamp + ".xlsx";
        link.click();
      } catch (error) {
        console.error("EXCEL出力に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    }
  }
};
</script>
